/**
 * Per-channel page — tabs: Overview, Config, Team.
 * Phase A scope. Phase B (Content pipeline) will plug in here.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft, RefreshCw, Settings, Users, BarChart3, Youtube, Loader2,
  AlertTriangle, Save, Trash2, Plus, Shield, ChevronDown, Layers, Calendar,
  Link2, CheckCircle2, Wand2, Mic, Square, X, Edit2,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StudioAIPanel from "./StudioAIPanel";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const ROLE_LABELS = {
  owner: "Owner",
  secondary_admin: "Secondary admin",
  channel_manager: "Channel manager",
  creator: "Creator",
  editor: "Editor",
  designer: "Designer",
  smm: "SMM",
  reviewer: "Reviewer",
  client: "Client",
};

export default function StudioChannel() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [ch, setCh] = useState(null);
  const [recentUploads, setRecentUploads] = useState([]);
  const [members, setMembers] = useState([]);
  const [boot, setBoot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [bRes, cRes, mRes] = await Promise.all([
        axios.get(`${API}/api/studio/bootstrap`, { withCredentials: true }),
        axios.get(`${API}/api/studio/channels/${channelId}`, { withCredentials: true }),
        axios.get(`${API}/api/studio/channels/${channelId}/members`, { withCredentials: true }),
      ]);
      setBoot(bRes.data);
      setCh(cRes.data);
      setMembers(mRes.data.members || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [channelId]);

  const syncFromYouTube = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/studio/channels/${channelId}/refresh`, {}, { withCredentials: true });
      setCh(r.data);
      setRecentUploads(r.data.recent_uploads || []);
      toast.success("Synced from YouTube");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed — channel may need re-auth.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <ShellWithLoader />;
  }
  if (error || !ch) {
    return (
      <>
        <Header />
        <div className="studio-shell">
          <div className="ac-error" data-testid="studio-ch-error">
            <AlertTriangle size={16}/> {error || "Channel not found"}
          </div>
          <button className="studio-btn-ghost" onClick={() => navigate("/studio")}>
            <ArrowLeft size={14}/> Back to Studio
          </button>
        </div>
        <Footer />
      </>
    );
  }

  const isOwner = ch.my_role === "owner";
  const isSuperAdmin = !!(boot?.user?.is_super_admin || boot?.user?.role === "super_admin");

  return (
    <>
      <Header />
      <div className="studio-shell">
        <button className="studio-back" onClick={() => navigate("/studio")} data-testid="studio-back">
          <ArrowLeft size={14}/> Back to channels
        </button>

        <div className="studio-channel-hero">
          {ch.thumbnail_url ? (
            <img src={ch.thumbnail_url} alt="" className="studio-channel-avatar studio-channel-avatar--lg"/>
          ) : (
            <div className="studio-channel-avatar studio-channel-avatar--lg studio-channel-avatar--ph"><Youtube size={28}/></div>
          )}
          <div className="studio-channel-hero-meta">
            <h1>{ch.title}</h1>
            <p>{ch.handle || ch.channel_id}</p>
            <div className="studio-channel-hero-stats">
              <span><strong>{ch.stats?.subscribers ?? 0}</strong> subs</span>
              <span><strong>{ch.stats?.videos ?? 0}</strong> videos</span>
              <span><strong>{ch.stats?.views ?? 0}</strong> total views</span>
            </div>
          </div>
          <div className="studio-channel-hero-actions">
            <Link className="studio-btn-ghost" to={`/studio/channels/${ch.channel_id}/sponsorships`} data-testid="studio-ch-sponsorships">
              💼 Sponsorships
            </Link>
            <button className="studio-btn-ghost" onClick={syncFromYouTube} disabled={busy} data-testid="studio-ch-sync">
              {busy ? <Loader2 size={14} className="ac-spin"/> : <RefreshCw size={14}/>}
              Sync from YouTube
            </button>
          </div>
        </div>

        <div className="studio-tabs" role="tablist" id="studio-channel-tabs">
          <TabBtn id="overview" current={tab} setTab={setTab} icon={<BarChart3 size={14}/>}>Overview</TabBtn>
          <TabBtn id="content" current={tab} setTab={setTab} icon={<Layers size={14}/>}>Content pipeline</TabBtn>
          <TabBtn id="planner" current={tab} setTab={setTab} icon={<Calendar size={14}/>}>Monthly planner</TabBtn>
          <TabBtn id="config" current={tab} setTab={setTab} icon={<Settings size={14}/>}>Configuration</TabBtn>
          <TabBtn id="team" current={tab} setTab={setTab} icon={<Users size={14}/>}>Team & permissions</TabBtn>
        </div>

        {tab === "overview" && (
          <OverviewTab ch={ch} recentUploads={recentUploads} onSync={syncFromYouTube} busy={busy}/>
        )}
        {tab === "content" && (
          <ContentTab ch={ch} permissions={ch.my_permissions || []} isOwner={isOwner} isSuperAdmin={isSuperAdmin}/>
        )}
        {tab === "planner" && (
          <PlannerTab ch={ch}/>
        )}
        {tab === "config" && (
          <ConfigTab ch={ch} canEdit={isOwner || (ch.my_permissions || []).includes("manage_brand_kit")} onSaved={loadAll}/>
        )}
        {tab === "team" && (
          <TeamTab
            ch={ch}
            members={members}
            permissionKeys={boot?.permission_keys || []}
            rolePresets={boot?.role_presets || {}}
            canManage={isOwner || (ch.my_permissions || []).includes("manage_team")}
            onChange={loadAll}
          />
        )}
      </div>
      <Footer />
    </>
  );
}

function TabBtn({ id, current, setTab, icon, children }) {
  return (
    <button
      type="button"
      role="tab"
      className="studio-tab"
      data-active={current === id}
      onClick={(e) => {
        e.preventDefault();
        if (current === id) return;
        setTab(id);
        // After re-render, scroll the tab bar to the top of the viewport.
        // This is a better UX than blind scroll preservation because content
        // can shrink/grow between tabs.
        requestAnimationFrame(() => {
          const el = document.getElementById("studio-channel-tabs");
          if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
          }
        });
      }}
      data-testid={`studio-tab-${id}`}
    >
      {icon} {children}
    </button>
  );
}

function ShellWithLoader() {
  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-card studio-loading">
          <Loader2 size={22} className="ac-spin" style={{ color: "var(--ac-purple, #a78bfa)" }}/>
          <p>Loading channel…</p>
        </div>
      </div>
      <Footer />
    </>
  );
}

function OverviewTab({ ch, recentUploads, onSync, busy }) {
  return (
    <div className="studio-tab-body">
      <div className="studio-grid studio-grid--2">
        <div className="studio-card">
          <h3>Channel snapshot</h3>
          <ul className="studio-kv">
            <li><span>Niche</span><strong>{ch.config?.niche || "Not set"}</strong></li>
            <li><span>Audience</span><strong>{ch.config?.audience || "Not set"}</strong></li>
            <li><span>Voice</span><strong>{ch.config?.voice || "Not set"}</strong></li>
            <li><span>Long target / mo</span><strong>{ch.config?.monthly_targets?.long ?? 8}</strong></li>
            <li><span>Shorts target / mo</span><strong>{ch.config?.monthly_targets?.short ?? 30}</strong></li>
          </ul>
        </div>
        <div className="studio-card">
          <h3>Recent uploads <button className="studio-link" onClick={onSync} disabled={busy}>refresh</button></h3>
          {recentUploads.length === 0 ? (
            <p className="studio-empty-inline">
              Click &quot;Sync from YouTube&quot; to pull recent uploads.
            </p>
          ) : (
            <ul className="studio-uploads">
              {recentUploads.slice(0, 8).map((v) => (
                <li key={v.video_id}>
                  {v.thumbnail_url && <img src={v.thumbnail_url} alt=""/>}
                  <div>
                    <p>{v.title}</p>
                    <small>{v.published_at ? new Date(v.published_at).toLocaleDateString() : ""}</small>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ConfigTab({ ch, canEdit, onSaved }) {
  const [config, setConfig] = useState(ch.config || {});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));
  const setTarget = (k, v) => setConfig((c) => ({ ...c, monthly_targets: { ...(c.monthly_targets || {}), [k]: Number(v) || 0 } }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/studio/channels/${ch.channel_id}/config`, config, { withCredentials: true });
      toast.success("Channel config saved");
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="studio-tab-body">
      <div className="studio-card">
        <h3>Channel configuration</h3>
        <p className="studio-card-sub">
          MarkeeMix AI uses these settings to keep every script + SEO + thumbnail brief on-brand.
        </p>
        <div className="studio-form">
          <Field label="Niche" testId="cfg-niche">
            <div style={{ display: "flex", gap: 8 }}>
              <input style={{ flex: 1 }} value={config.niche || ""} onChange={(e) => set("niche", e.target.value)} disabled={!canEdit} placeholder="e.g. tech tutorials"/>
              {canEdit && (
                <button
                  type="button"
                  className="studio-btn-ghost"
                  data-testid="cfg-ai-niche-suggest"
                  onClick={async () => {
                    if (!config.niche || config.niche.length < 3) {
                      toast.error("Type your niche first (at least 3 chars)");
                      return;
                    }
                    try {
                      toast.info("AI is analysing your niche…");
                      const r = await axios.post(`${API}/api/studio/ai/niche-suggest`,
                        { channel_id: ch.channel_id, niche: config.niche },
                        { withCredentials: true });
                      const d = r.data || {};
                      setConfig((c) => ({
                        ...c,
                        audience: c.audience || d.audience || "",
                        voice: c.voice || d.brand_voice || "",
                        tone: c.tone || d.brand_voice || "",
                        target_keywords: c.target_keywords?.length ? c.target_keywords : (d.primary_keywords || []),
                        competitor_channels: c.competitor_channels?.length ? c.competitor_channels : (d.competitor_channels || []),
                        title_format: c.title_format || d.title_format || "",
                        thumbnail_style: c.thumbnail_style || d.thumbnail_style || "",
                        cta_style: c.cta_style || d.cta_style || "",
                        content_pillars: c.content_pillars?.length ? c.content_pillars : (d.content_pillars || []),
                        monthly_targets: (c.monthly_targets?.long || c.monthly_targets?.short) ? c.monthly_targets : (d.posting_schedule || {}),
                      }));
                      toast.success("AI suggestions filled in. Review and save.");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "AI suggestion failed");
                    }
                  }}
                >
                  <Wand2 size={14}/> AI suggest
                </button>
              )}
            </div>
          </Field>
          <Field label="Target audience" testId="cfg-audience">
            <input value={config.audience || ""} onChange={(e) => set("audience", e.target.value)} disabled={!canEdit} placeholder="e.g. Indian developers 18-30"/>
          </Field>
          <Field label="Brand voice" testId="cfg-voice">
            <input value={config.voice || ""} onChange={(e) => set("voice", e.target.value)} disabled={!canEdit} placeholder="e.g. friendly, technical, no jargon"/>
          </Field>
          <Field label="Tone" testId="cfg-tone">
            <input value={config.tone || ""} onChange={(e) => set("tone", e.target.value)} disabled={!canEdit} placeholder="e.g. energetic"/>
          </Field>
          <Field label="Target keywords (comma-separated)" testId="cfg-keywords">
            <input
              value={(config.target_keywords || []).join(", ")}
              onChange={(e) => set("target_keywords", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              disabled={!canEdit}
              placeholder="seo, content marketing, ai tools"
            />
          </Field>
          <Field label="Competitor channels (comma-separated handles/URLs)" testId="cfg-competitors">
            <input
              value={(config.competitors || []).join(", ")}
              onChange={(e) => set("competitors", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              disabled={!canEdit}
              placeholder="@channelA, @channelB"
            />
          </Field>
          <div className="studio-form-row">
            <Field label="Monthly long videos" testId="cfg-target-long">
              <input type="number" min={0} value={config.monthly_targets?.long ?? 8} onChange={(e) => setTarget("long", e.target.value)} disabled={!canEdit}/>
            </Field>
            <Field label="Monthly shorts" testId="cfg-target-short">
              <input type="number" min={0} value={config.monthly_targets?.short ?? 30} onChange={(e) => setTarget("short", e.target.value)} disabled={!canEdit}/>
            </Field>
          </div>
          <Field label="Preferred title format" testId="cfg-title-format">
            <input value={config.title_format || ""} onChange={(e) => set("title_format", e.target.value)} disabled={!canEdit} placeholder="e.g. How to {X} in {Y} minutes"/>
          </Field>
          <Field label="Preferred thumbnail style" testId="cfg-thumb-style">
            <input value={config.thumbnail_style || ""} onChange={(e) => set("thumbnail_style", e.target.value)} disabled={!canEdit} placeholder="big-face + bold text"/>
          </Field>
          <Field label="CTA style" testId="cfg-cta">
            <input value={config.cta_style || ""} onChange={(e) => set("cta_style", e.target.value)} disabled={!canEdit} placeholder="subscribe + comment what you want next"/>
          </Field>
        </div>
        {canEdit && (
          <div className="studio-form-actions">
            <button className="studio-btn-primary" onClick={save} disabled={saving} data-testid="cfg-save">
              {saving ? <Loader2 size={14} className="ac-spin"/> : <Save size={14}/>} Save configuration
            </button>
          </div>
        )}
      </div>
      <ApprovalFlowEditor ch={ch} canEdit={canEdit}/>
      <PublicShareCard ch={ch} canEdit={canEdit}/>
    </div>
  );
}

function PublicShareCard({ ch, canEdit }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/api/studio/channels/${ch.channel_id}/public-share/status`, { withCredentials: true })
      .then((r) => { if (alive) setStatus(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [ch.channel_id]);

  const enable = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/studio/channels/${ch.channel_id}/public-share/enable`,
        {}, { withCredentials: true }
      );
      setStatus({ enabled: true, slug: r.data.slug });
      toast.success("Public client portal enabled");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/studio/channels/${ch.channel_id}/public-share/disable`,
        {}, { withCredentials: true }
      );
      setStatus({ enabled: false, slug: status?.slug });
      toast.success("Public client portal disabled");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const url = status?.slug ? `${window.location.origin}/r/${status.slug}` : "";
  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch (e) { toast.error("Could not copy"); }
  };

  return (
    <div className="studio-card" style={{ marginTop: 18 }} data-testid="public-share-card">
      <h3>🌐 Public client portal</h3>
      <p className="studio-card-sub">
        Generate a read-only share URL for your client — they can see the pipeline,
        upcoming videos, top performers and pending approvals without logging in.
      </p>
      {!status ? (
        <p className="studio-muted">Loading…</p>
      ) : status.enabled ? (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
            <input
              className="studio-input"
              value={url}
              readOnly
              style={{ flex: 1, minWidth: 280 }}
              data-testid="public-share-url"
            />
            <button className="studio-btn-ghost" onClick={copyUrl} data-testid="public-share-copy">
              Copy link
            </button>
            <a className="studio-btn-ghost" href={url} target="_blank" rel="noreferrer" data-testid="public-share-open">
              Open client view ↗
            </a>
            {canEdit && (
              <button className="studio-btn-ghost" onClick={disable} disabled={busy} data-testid="public-share-disable">
                {busy ? <Loader2 size={14} className="ac-spin"/> : <X size={14}/>} Disable
              </button>
            )}
          </div>
          <small className="studio-muted">Anyone with this link can view (read-only). Disable to revoke.</small>
        </>
      ) : (
        canEdit ? (
          <button className="studio-btn-primary" onClick={enable} disabled={busy} data-testid="public-share-enable">
            {busy ? <Loader2 size={14} className="ac-spin"/> : <Plus size={14}/>} Enable client portal
          </button>
        ) : (
          <p className="studio-muted">Only the channel owner can enable the client portal.</p>
        )
      )}
    </div>
  );
}

function ApprovalFlowEditor({ ch, canEdit }) {
  const ALL_STEPS = ["creator", "editor", "designer", "smm"];
  const [flow, setFlow] = useState([]);
  const [available, setAvailable] = useState(ALL_STEPS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/studio/channels/${ch.channel_id}/approval-flow`, { withCredentials: true });
        setFlow(r.data.approval_flow || []);
        setAvailable(r.data.available_steps || ALL_STEPS);
      } catch (_) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ch.channel_id]);

  const toggle = (step) => {
    setFlow((f) => f.includes(step) ? f.filter((x) => x !== step) : [...f, step]);
  };

  const move = (idx, dir) => {
    setFlow((f) => {
      const next = [...f];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return next;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/studio/channels/${ch.channel_id}/approval-flow`,
        { approval_flow: flow }, { withCredentials: true });
      toast.success("Approval flow saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="studio-card" style={{ marginTop: 18 }} data-testid="approval-flow-card">
      <h3>Approval flow</h3>
      <p className="studio-card-sub">
        Decide who must sign off before a video can move forward. Empty = no approvals required.
      </p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
        {available.map((s) => (
          <button key={s} className="studio-chip" data-active={flow.includes(s)} onClick={() => canEdit && toggle(s)} data-testid={`flow-toggle-${s}`}>
            {s}
          </button>
        ))}
      </div>
      {flow.length > 0 && (
        <ol style={{ marginTop: 14, padding: 0, listStyle: "none" }} data-testid="approval-flow-order">
          {flow.map((s, i) => (
            <li key={s} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(255,255,255,.04)", borderRadius: 10, marginBottom: 6 }}>
              <strong style={{ color: "var(--st-purple, #a78bfa)" }}>{i + 1}.</strong>
              <span style={{ flex: 1, textTransform: "capitalize" }}>{s}</span>
              {canEdit && i > 0 && <button className="studio-link" onClick={() => move(i, -1)}>↑</button>}
              {canEdit && i < flow.length - 1 && <button className="studio-link" onClick={() => move(i, 1)}>↓</button>}
            </li>
          ))}
        </ol>
      )}
      {canEdit && (
        <div className="studio-form-actions">
          <button className="studio-btn-primary" onClick={save} disabled={saving} data-testid="flow-save">
            {saving ? <Loader2 size={14} className="ac-spin"/> : <Save size={14}/>} Save flow
          </button>
        </div>
      )}
    </div>
  );
}

function TeamTab({ ch, members, permissionKeys, rolePresets, canManage, onChange }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("creator");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editPerms, setEditPerms] = useState([]);

  const invite = async () => {
    if (!email) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/studio/channels/${ch.channel_id}/members`,
        { email: email.trim(), phone: phone.trim() || null, name: name.trim() || null, role },
        { withCredentials: true });
      toast.success(
        r.data?.is_new_invite
          ? `Invite sent to ${email}. They'll see this channel when they sign in with Google.`
          : `${email} added as ${ROLE_LABELS[role] || role}`,
        { duration: 5000 }
      );
      setEmail(""); setPhone(""); setName("");
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (m) => {
    setEditing(m.user_id);
    setEditPerms(m.permissions || []);
  };

  const togglePerm = (k) =>
    setEditPerms((arr) => arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);

  const saveEdit = async (m) => {
    try {
      await axios.put(`${API}/api/studio/channels/${ch.channel_id}/members/${m.user_id}`,
        { permissions: editPerms }, { withCredentials: true });
      toast.success("Permissions updated");
      setEditing(null);
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  const changeRole = async (m, role) => {
    try {
      await axios.put(`${API}/api/studio/channels/${ch.channel_id}/members/${m.user_id}`,
        { role }, { withCredentials: true });
      toast.success("Role updated");
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change role");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.user?.email || m.user_id} from this channel?`)) return;
    try {
      await axios.delete(`${API}/api/studio/channels/${ch.channel_id}/members/${m.user_id}`,
        { withCredentials: true });
      toast.success("Removed");
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <div className="studio-tab-body">
      <div className="studio-card">
        <h3><Users size={16} style={{ verticalAlign: "-3px" }}/> Team members</h3>
        <p className="studio-card-sub">
          Add teammates by their MarkeeMix email. Each member gets per-channel permissions independently.
        </p>

        {canManage && (
          <div className="studio-invite-row" data-testid="team-invite-row">
            <input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="team-invite-name"
            />
            <input
              type="email"
              placeholder="teammate@example.com *"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="team-invite-email"
            />
            <input
              type="tel"
              placeholder="WhatsApp +91…"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="team-invite-phone"
            />
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="team-invite-role">
              {Object.keys(rolePresets || {}).filter((r) => r !== "owner").map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
              ))}
            </select>
            <button className="studio-btn-primary" onClick={invite} disabled={busy || !email} data-testid="team-invite-add">
              {busy ? <Loader2 size={14} className="ac-spin"/> : <Plus size={14}/>} Invite
            </button>
          </div>
        )}

        <table className="studio-team-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Permissions</th>
              {canManage && <th></th>}
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr><td colSpan={canManage ? 4 : 3} className="studio-empty-inline">
                No teammates yet. Add your first one above.
              </td></tr>
            )}
            {members.map((m) => (
              <tr key={m.user_id} data-testid={`team-row-${m.user_id}`}>
                <td>
                  <strong>{m.user?.name || m.user?.email || m.user_id}</strong>
                  {m.user?.email && <small>{m.user.email}</small>}
                </td>
                <td>
                  {canManage ? (
                    <select value={m.role} onChange={(e) => changeRole(m, e.target.value)} data-testid={`team-role-${m.user_id}`}>
                      {Object.keys(rolePresets || {}).filter((r) => r !== "owner").map((r) => (
                        <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`studio-role studio-role--${m.role}`}>{ROLE_LABELS[m.role] || m.role}</span>
                  )}
                </td>
                <td>
                  {editing === m.user_id ? (
                    <div className="studio-perm-grid">
                      {permissionKeys.map((k) => (
                        <label key={k} className="studio-perm">
                          <input
                            type="checkbox"
                            checked={editPerms.includes(k)}
                            onChange={() => togglePerm(k)}
                          />
                          <span>{k.replace(/_/g, " ")}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <details className="studio-perm-collapse">
                      <summary>{(m.permissions || []).length} permission(s) <ChevronDown size={12}/></summary>
                      <div className="studio-perm-chips">
                        {(m.permissions || []).map((p) => <span key={p} className="studio-pill studio-pill--sm">{p.replace(/_/g, " ")}</span>)}
                      </div>
                    </details>
                  )}
                </td>
                {canManage && (
                  <td className="studio-row-actions">
                    {editing === m.user_id ? (
                      <>
                        <button className="studio-link" onClick={() => saveEdit(m)} data-testid={`team-save-${m.user_id}`}>save</button>
                        <button className="studio-link" onClick={() => setEditing(null)}>cancel</button>
                      </>
                    ) : (
                      <button className="studio-link" onClick={() => openEdit(m)} data-testid={`team-edit-${m.user_id}`}>
                        <Shield size={12}/> edit
                      </button>
                    )}
                    <button className="studio-link studio-link--danger" onClick={() => remove(m)} data-testid={`team-remove-${m.user_id}`}>
                      <Trash2 size={12}/> remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="studio-field" data-testid={testId}>
      <span>{label}</span>
      {children}
    </label>
  );
}

const STAGE_LABEL = {
  idea: "Ideas", approved: "Approved", scripting: "Scripting",
  script_review: "Script review", recording: "Recording",
  raw_uploaded: "Raw uploaded", editing: "Editing",
  edit_review: "Edit review", thumbnail: "Thumbnail",
  thumbnail_review: "Thumb review", seo: "SEO",
  scheduled: "Scheduled", published: "Published", archived: "Archived",
};

const PIPELINE_STAGES = [
  "idea", "approved", "scripting", "script_review", "recording",
  "raw_uploaded", "editing", "edit_review", "thumbnail", "thumbnail_review",
  "seo", "scheduled", "published",
];

// SLA thresholds in hours per stage (mirrors backend studio_crm_health.py)
const SLA_HOURS = {
  raw_uploaded: 36, editing: 72, edit_review: 24,
  thumbnail: 48, thumbnail_review: 24, seo: 24, scheduled: 168,
};
function _itemAgeHours(it) {
  const upd = it.updated_at || it.created_at;
  if (!upd) return null;
  try { return (Date.now() - new Date(upd).getTime()) / 3600000; }
  catch (e) { return null; }
}
function slaClass(it) {
  const sla = SLA_HOURS[it.stage];
  const age = _itemAgeHours(it);
  if (!sla || age == null) return "";
  if (age > sla * 2) return "studio-kanban-card--critical";
  if (age > sla) return "studio-kanban-card--warning";
  if (age > sla * 0.66) return "studio-kanban-card--soon";
  return "";
}
function slaTitle(it) {
  const sla = SLA_HOURS[it.stage];
  const age = _itemAgeHours(it);
  if (!sla || age == null) return undefined;
  return `Stage age: ${Math.round(age)}h · SLA: ${sla}h`;
}

function ContentTab({ ch, permissions, isOwner, isSuperAdmin }) {
  // Super admin gets full UI parity with the backend `_can()` bypass.
  // Role-bundle members (creator/smm/editor/designer/manager/admin) are
  // surfaced by the new `ch.my_role` field whose default permission set
  // mirrors the backend role table.
  const roleBundle = (ch.my_role || "").toLowerCase();
  const roleAllowsEdit   = ["owner","secondary_admin","admin","manager","creator","smm","editor","designer","seo"].includes(roleBundle);
  const roleAllowsDelete = roleAllowsEdit;
  const canCreate = isSuperAdmin || isOwner || roleAllowsEdit || permissions.includes("create_videos");
  const canEdit   = isSuperAdmin || isOwner || roleAllowsEdit  || permissions.includes("edit_videos");
  const canDelete = isSuperAdmin || isOwner || roleAllowsDelete || permissions.includes("delete_videos");
  const canAI     = isSuperAdmin || isOwner || roleAllowsEdit
    || permissions.includes("generate_ai_scripts") || permissions.includes("generate_seo");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState("long");
  const [newPubDate, setNewPubDate] = useState("");
  const [adding, setAdding] = useState(false);
  const [aiOpen, setAiOpen] = useState(null); // current content item or null
  const [editing, setEditing] = useState(null); // content item being edited
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("studio.contentView") || "list");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/studio/content/by-channel/${ch.channel_id}`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load content");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [ch.channel_id]);

  const create = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/api/studio/content`, {
        channel_id: ch.channel_id,
        title: newTitle.trim(),
        type: newType,
        publish_date: newPubDate ? new Date(newPubDate).toISOString() : null,
      }, { withCredentials: true });
      toast.success("Video idea added");
      setNewTitle(""); setNewPubDate(""); setShowNew(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
    } finally {
      setAdding(false);
    }
  };

  const moveStage = async (item, newStage) => {
    try {
      await axios.post(`${API}/api/studio/content/${item._id}/stage`, { stage: newStage }, { withCredentials: true });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not move");
    }
  };

  const attachDrive = async (item, kind) => {
    const url = window.prompt(`Paste the Google Drive URL for the ${kind} video:`);
    if (!url) return;
    try {
      await axios.post(`${API}/api/studio/content/${item._id}/drive`, { kind, url }, { withCredentials: true });
      toast.success(`${kind === "raw" ? "Raw" : "Edited"} video attached`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid Drive URL");
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This moves it to Trash (Super admin can restore).`)) return;
    try {
      // Soft-delete preserves the row in studio_content_trash for restore.
      await axios.post(`${API}/api/studio/content/${item._id}/soft-delete`, {}, { withCredentials: true });
      toast.success("Moved to trash");
      load();
    } catch (err) {
      // Fallback to hard-delete if soft-delete unavailable for this role.
      try {
        await axios.delete(`${API}/api/studio/content/${item._id}`, { withCredentials: true });
        toast.success("Deleted");
        load();
      } catch (e2) {
        toast.error(e2?.response?.data?.detail || err?.response?.data?.detail || "Could not delete");
      }
    }
  };

  const saveEdit = async (updates) => {
    if (!editing) return;
    try {
      await axios.put(`${API}/api/studio/content/${editing._id}`, updates, { withCredentials: true });
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  const setView = (m) => { setViewMode(m); localStorage.setItem("studio.contentView", m); };

  const byStage = PIPELINE_STAGES.reduce((acc, s) => { acc[s] = []; return acc; }, {});
  for (const it of items) {
    if (byStage[it.stage]) byStage[it.stage].push(it);
    else byStage.idea.push(it);
  }

  return (
    <div className="studio-tab-body">
      <div className="studio-pipeline-head">
        <h3>Content pipeline · {items.length} item(s)</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* View toggle */}
          <div data-testid="content-view-toggle" style={{ display: "inline-flex", borderRadius: 8, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
            <button
              onClick={() => setView("list")}
              data-testid="content-view-list"
              style={{
                padding: "6px 12px", fontSize: 12, border: 0, cursor: "pointer",
                background: viewMode === "list" ? "rgba(167,139,250,0.25)" : "transparent",
                color: viewMode === "list" ? "#0F172A" : "#64748B",
              }}
            >☰ List</button>
            <button
              onClick={() => setView("kanban")}
              data-testid="content-view-kanban"
              style={{
                padding: "6px 12px", fontSize: 12, border: 0, cursor: "pointer",
                background: viewMode === "kanban" ? "rgba(167,139,250,0.25)" : "transparent",
                color: viewMode === "kanban" ? "#0F172A" : "#64748B",
              }}
            >▦ Kanban</button>
          </div>
          {canCreate && (
            <>
              <VoiceIdeaButton channelId={ch.channel_id} onAdded={load}/>
              <button className="studio-btn-primary" onClick={() => setShowNew(true)} data-testid="content-add">
                <Plus size={14}/> Add video
              </button>
            </>
          )}
        </div>
      </div>

      {showNew && (
        <div className="studio-card studio-card--new" data-testid="content-new-card">
          <div className="studio-form">
            <Field label="Title" testId="content-new-title">
              <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. 10 SEO mistakes that kill your channel"/>
            </Field>
            <div className="studio-form-row">
              <Field label="Type" testId="content-new-type">
                <select value={newType} onChange={(e) => setNewType(e.target.value)}>
                  <option value="long">Long video</option>
                  <option value="short">Short</option>
                  <option value="reel">Reel</option>
                  <option value="blog">Blog</option>
                </select>
              </Field>
              <Field label="Publish date (optional)" testId="content-new-date">
                <input type="date" value={newPubDate} onChange={(e) => setNewPubDate(e.target.value)}/>
              </Field>
            </div>
            <div className="studio-form-actions">
              <button className="studio-btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
              <button className="studio-btn-primary" onClick={create} disabled={adding || !newTitle.trim()} data-testid="content-new-save">
                {adding ? <Loader2 size={14} className="ac-spin"/> : <Save size={14}/>} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="studio-card studio-loading">
          <Loader2 size={22} className="ac-spin" style={{ color: "var(--st-purple)" }}/>
        </div>
      ) : items.length === 0 ? (
        <div className="studio-empty-inline studio-card" style={{ textAlign: "center", padding: 36 }}>
          <p style={{ color: "var(--st-text-soft)" }}>No videos yet. Click &quot;Add video&quot; to start the pipeline.</p>
        </div>
      ) : viewMode === "kanban" ? (
        <div className="studio-kanban" data-testid="studio-kanban">
          {PIPELINE_STAGES.map((stage) => (
            <div
              key={stage}
              className="studio-kanban-col"
              data-testid={`kanban-col-${stage}`}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("studio-kanban-col--drop"); }}
              onDragLeave={(e) => e.currentTarget.classList.remove("studio-kanban-col--drop")}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("studio-kanban-col--drop");
                const id = e.dataTransfer.getData("text/content-id");
                const fromStage = e.dataTransfer.getData("text/from-stage");
                if (id && fromStage !== stage) {
                  const item = items.find((i) => i._id === id);
                  if (item) moveStage(item, stage);
                }
              }}
            >
              <header>
                <strong>{STAGE_LABEL[stage]}</strong>
                <small data-testid={`kanban-col-count-${stage}`}>{byStage[stage].length}</small>
              </header>
              {byStage[stage].map((it) => (
                <div
                  key={it._id}
                  draggable={canEdit}
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/content-id", it._id);
                    e.dataTransfer.setData("text/from-stage", it.stage);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  className={`studio-kanban-card-wrap ${slaClass(it)}`}
                  title={slaTitle(it)}
                  data-testid={`kanban-card-${it._id}`}
                  style={{ cursor: canEdit ? "grab" : "default" }}
                >
                  <ContentCard
                    item={it}
                    canEdit={canEdit} canDelete={canDelete} canAI={canAI}
                    onMove={moveStage} onAttachDrive={attachDrive} onAI={setAiOpen}
                    onEdit={setEditing} onDelete={deleteItem}
                  />
                </div>
              ))}
              {byStage[stage].length === 0 && (
                <div className="studio-kanban-empty" data-testid={`kanban-empty-${stage}`}>
                  Drop here
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <ContentListView
          items={items}
          canEdit={canEdit} canDelete={canDelete} canAI={canAI}
          onMove={moveStage} onAI={setAiOpen}
          onEdit={setEditing} onDelete={deleteItem}
        />
      )}

      {editing && (
        <ContentEditModal
          item={editing}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}

      <StudioAIPanel
        open={!!aiOpen}
        onClose={() => setAiOpen(null)}
        channelId={ch.channel_id}
        contentItem={aiOpen}
        onSaved={() => { setAiOpen(null); load(); }}
      />
    </div>
  );
}

function ContentCard({ item, canEdit, canDelete, canAI, onMove, onAttachDrive, onAI, onEdit, onDelete }) {
  const [busyAction, setBusyAction] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);

  // Derive the best playable URL: prefer YouTube ID > edited > raw
  const playable = (() => {
    if (item.youtube_video_id) {
      return { kind: "youtube", url: `https://www.youtube.com/embed/${item.youtube_video_id}?autoplay=1` };
    }
    const driveUrl = item.drive?.edited_url || item.drive?.raw_url;
    if (driveUrl) {
      // Convert Google Drive share links to embeddable preview URL.
      const m = String(driveUrl).match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (m) {
        return { kind: "drive", url: `https://drive.google.com/file/d/${m[1]}/preview` };
      }
      return { kind: "drive", url: driveUrl };
    }
    return null;
  })();

  const publish = async (scheduleAt = null) => {
    setBusyAction("publish");
    try {
      const r = await axios.post(`${API}/api/studio/publish`, {
        content_id: item._id, schedule_at: scheduleAt, privacy: "public",
      }, { withCredentials: true });
      toast.success(scheduleAt ? "Scheduled on YouTube" : "Published to YouTube ✓");
      window.location.reload();
      return r;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    } finally { setBusyAction(null); }
  };

  const repurpose = async () => {
    setBusyAction("repurpose");
    try {
      const r = await axios.post(`${API}/api/studio/repurpose`, { content_id: item._id }, { withCredentials: true });
      toast.success(`${r.data.count} repurpose tasks created`);
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Repurpose failed");
    } finally { setBusyAction(null); }
  };

  const pullPerf = async () => {
    setBusyAction("perf");
    try {
      const r = await axios.post(`${API}/api/studio/performance/${item._id}`, {}, { withCredentials: true });
      const v = r.data;
      const ai = v.ai_critique || {};
      toast.success(`Views ${v.views || 0} · likes ${v.likes || 0} · verdict: ${ai.verdict || "—"}`);
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Performance pull failed");
    } finally { setBusyAction(null); }
  };

  return (
    <div className="studio-content-card" id={`content-${item._id}`} data-testid={`content-${item._id}`}>
      <h4>{item.title}</h4>
      <div className="studio-content-card-meta">
        <span className="studio-pill studio-pill--sm">{item.type}</span>
        {item.publish_date && (
          <span className="studio-pill studio-pill--sm">
            <Calendar size={10} style={{ verticalAlign: "-1px" }}/> {new Date(item.publish_date).toLocaleDateString()}
          </span>
        )}
        {item.performance?.ai_critique?.verdict && (
          <span className="studio-pill studio-pill--sm" title="AI verdict">
            🎯 {item.performance.ai_critique.verdict}
          </span>
        )}
        {playable && (
          <button
            className="studio-pill studio-pill--sm"
            onClick={() => setShowPlayer((v) => !v)}
            data-testid={`content-play-${item._id}`}
            style={{ cursor: "pointer", border: "1px solid rgba(167,139,250,0.3)", background: "rgba(167,139,250,0.1)" }}
            title={playable.kind === "youtube" ? "Play YouTube video" : "Preview draft from Drive"}
          >
            ▶ {showPlayer ? "Hide" : (playable.kind === "youtube" ? "Play" : "Preview draft")}
          </button>
        )}
      </div>
      {showPlayer && playable && (
        <div
          className="studio-vid-embed"
          data-testid={`content-player-${item._id}`}
          style={{ marginTop: 10, borderRadius: 10, overflow: "hidden", aspectRatio: "16 / 9" }}
        >
          <iframe
            src={playable.url}
            title={item.title}
            allow="autoplay; encrypted-media"
            allowFullScreen
            style={{ width: "100%", height: "100%", border: 0 }}
          />
        </div>
      )}
      {item.drive?.raw_url && (
        <a href={item.drive.raw_url} target="_blank" rel="noreferrer" className="studio-link">
          <Link2 size={12}/> raw
        </a>
      )}
      {item.drive?.edited_url && (
        <a href={item.drive.edited_url} target="_blank" rel="noreferrer" className="studio-link">
          <Link2 size={12}/> edited
        </a>
      )}
      {item.ppt_url && (
        <a href={`${API}${item.ppt_url}`} target="_blank" rel="noreferrer" className="studio-link">
          📊 PPT
        </a>
      )}
      {canEdit && (
        <div className="studio-content-actions">
          {canAI && (
            <button className="studio-link studio-link--ai" onClick={() => onAI(item)} data-testid={`content-ai-${item._id}`}>
              <Wand2 size={12}/> Generate with AI
            </button>
          )}
          {!item.drive?.raw_url && (
            <button className="studio-link" onClick={() => onAttachDrive(item, "raw")} data-testid={`content-attach-raw-${item._id}`}>
              <Link2 size={12}/> Attach raw
            </button>
          )}
          {item.drive?.raw_url && !item.drive?.edited_url && (
            <button className="studio-link" onClick={() => onAttachDrive(item, "edited")} data-testid={`content-attach-edited-${item._id}`}>
              <Link2 size={12}/> Attach edited
            </button>
          )}
          {item.stage === "seo" && item.youtube_video_id && (
            <button className="studio-link studio-link--ai" onClick={() => publish()} disabled={busyAction === "publish"} data-testid={`content-publish-${item._id}`}>
              {busyAction === "publish" ? <Loader2 size={12} className="ac-spin"/> : "🚀"} Publish
            </button>
          )}
          {item.stage === "published" && (
            <>
              <button className="studio-link" onClick={pullPerf} disabled={busyAction === "perf"} data-testid={`content-perf-${item._id}`}>
                {busyAction === "perf" ? <Loader2 size={12} className="ac-spin"/> : "📈"} Pull perf
              </button>
              {!item.repurpose_spawned && (
                <button className="studio-link" onClick={repurpose} disabled={busyAction === "repurpose"} data-testid={`content-repurpose-${item._id}`}>
                  {busyAction === "repurpose" ? <Loader2 size={12} className="ac-spin"/> : "♻️"} Repurpose
                </button>
              )}
            </>
          )}
          <select
            className="studio-stage-select"
            value={item.stage}
            onChange={(e) => onMove(item, e.target.value)}
            data-testid={`content-stage-${item._id}`}
          >
            {PIPELINE_STAGES.map((s) => (
              <option key={s} value={s}>{STAGE_LABEL[s]}</option>
            ))}
          </select>
          {onEdit && (
            <button
              className="studio-link"
              onClick={() => onEdit(item)}
              data-testid={`content-edit-${item._id}`}
              title="Edit this row"
            >
              <Edit2 size={12}/> Edit
            </button>
          )}
          {canDelete && onDelete && (
            <button
              className="studio-link studio-link--danger"
              onClick={() => onDelete(item)}
              data-testid={`content-delete-${item._id}`}
              title="Delete (moves to trash)"
            >
              <Trash2 size={12}/> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}


/* ───────────────────────────────────────────────────────────────────────── */
/* ContentListView — table/list mode for the pipeline                         */
/* ───────────────────────────────────────────────────────────────────────── */
function ContentListView({ items, canEdit, canDelete, canAI, onMove, onAI, onEdit, onDelete }) {
  const sorted = [...items].sort((a, b) => {
    const ad = a.publish_date ? new Date(a.publish_date).getTime() : 0;
    const bd = b.publish_date ? new Date(b.publish_date).getTime() : 0;
    return bd - ad;
  });

  return (
    <div className="studio-card" data-testid="content-list-view" style={{ padding: 0, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "rgba(255,255,255,0.03)", textAlign: "left" }}>
            <th style={th}>Title</th>
            <th style={th}>Type</th>
            <th style={th}>Stage</th>
            <th style={th}>Publish date</th>
            <th style={th}>Drive</th>
            <th style={{ ...th, textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((it) => (
            <tr key={it._id} data-testid={`content-row-${it._id}`} style={{ borderBottom: "1px dashed rgba(255,255,255,0.06)" }}>
              <td style={td}>
                <strong>{it.title}</strong>
                {it.performance?.ai_critique?.verdict && (
                  <small style={{ display: "block", color: "#64748B" }}>🎯 {it.performance.ai_critique.verdict}</small>
                )}
              </td>
              <td style={td}><span className="studio-pill studio-pill--sm">{it.type}</span></td>
              <td style={td}>
                <select
                  className="studio-stage-select"
                  value={it.stage}
                  onChange={(e) => onMove(it, e.target.value)}
                  data-testid={`content-stage-list-${it._id}`}
                  disabled={!canEdit}
                  style={{ minWidth: 130 }}
                >
                  {PIPELINE_STAGES.map((s) => (
                    <option key={s} value={s}>{STAGE_LABEL[s]}</option>
                  ))}
                </select>
              </td>
              <td style={td}>{it.publish_date ? new Date(it.publish_date).toLocaleDateString() : <span style={{ color: "#94A3B8" }}>—</span>}</td>
              <td style={td}>
                <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {it.drive?.raw_url ? (
                    <a href={it.drive.raw_url} target="_blank" rel="noreferrer" className="studio-link" title="Raw">
                      <Link2 size={11}/> raw
                    </a>
                  ) : <span style={{ color: "#94A3B8", fontSize: 11 }}>—</span>}
                  {it.drive?.edited_url && (
                    <a href={it.drive.edited_url} target="_blank" rel="noreferrer" className="studio-link" title="Edited">
                      <Link2 size={11}/> edited
                    </a>
                  )}
                </span>
              </td>
              <td style={{ ...td, textAlign: "right" }}>
                <span style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  {canAI && (
                    <button className="studio-link studio-link--ai" onClick={() => onAI(it)} data-testid={`content-ai-list-${it._id}`}>
                      <Wand2 size={11}/> AI
                    </button>
                  )}
                  {canEdit && (
                    <button className="studio-link" onClick={() => onEdit(it)} data-testid={`content-edit-list-${it._id}`}>
                      <Edit2 size={11}/> Edit
                    </button>
                  )}
                  {canDelete && (
                    <button className="studio-link studio-link--danger" onClick={() => onDelete(it)} data-testid={`content-delete-list-${it._id}`}>
                      <Trash2 size={11}/> Delete
                    </button>
                  )}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const th = { padding: "10px 12px", fontWeight: 600, color: "#64748B", borderBottom: "2px solid rgba(255,255,255,0.08)" };
const td = { padding: "10px 12px", verticalAlign: "top" };


/* ───────────────────────────────────────────────────────────────────────── */
/* ContentEditModal — inline editor for title / type / publish_date / notes   */
/* ───────────────────────────────────────────────────────────────────────── */
function ContentEditModal({ item, onClose, onSave }) {
  const [title, setTitle] = useState(item.title || "");
  const [type, setType] = useState(item.type || "long");
  const [publishDate, setPublishDate] = useState(item.publish_date ? new Date(item.publish_date).toISOString().slice(0, 10) : "");
  const [notes, setNotes] = useState(item.notes || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        type,
        publish_date: publishDate ? new Date(publishDate).toISOString() : null,
        notes,
      });
    } finally { setSaving(false); }
  };

  return (
    <div
      onClick={onClose}
      data-testid="content-edit-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 120,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "60px 16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="studio-card"
        style={{ padding: 22, maxWidth: 560, width: "100%" }}
      >
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <span className="studio-eyebrow"><Edit2 size={12} /> Edit content</span>
            <h3 style={{ margin: "4px 0 0" }}>Update fields</h3>
          </div>
          <button className="studio-btn-ghost" onClick={onClose} data-testid="content-edit-close"><X size={14}/></button>
        </header>

        <label style={{ display: "block", marginBottom: 12 }}>
          <span style={{ display: "block", fontSize: 12, color: "#64748B", marginBottom: 4 }}>Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="content-edit-title"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#0F172A", fontSize: 14 }}
          />
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <label>
            <span style={{ display: "block", fontSize: 12, color: "#64748B", marginBottom: 4 }}>Type</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              data-testid="content-edit-type"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#0F172A", fontSize: 14 }}
            >
              <option value="long">Long video</option>
              <option value="short">Short</option>
              <option value="reel">Reel</option>
              <option value="blog">Blog</option>
            </select>
          </label>
          <label>
            <span style={{ display: "block", fontSize: 12, color: "#64748B", marginBottom: 4 }}>Publish date</span>
            <input
              type="date"
              value={publishDate}
              onChange={(e) => setPublishDate(e.target.value)}
              data-testid="content-edit-date"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#0F172A", fontSize: 14 }}
            />
          </label>
        </div>
        <label style={{ display: "block", marginBottom: 14 }}>
          <span style={{ display: "block", fontSize: 12, color: "#64748B", marginBottom: 4 }}>Notes</span>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="content-edit-notes"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#0F172A", fontSize: 14, resize: "vertical" }}
          />
        </label>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="studio-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="studio-btn-primary"
            onClick={submit}
            disabled={saving || !title.trim()}
            data-testid="content-edit-save"
          >
            {saving ? <Loader2 size={14} className="ac-spin"/> : <Save size={14}/>} Save changes
          </button>
        </div>
      </div>
    </div>
  );
}


function PlannerTab({ ch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/studio/content/planner/${ch.channel_id}`, { withCredentials: true });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load planner");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [ch.channel_id]);

  if (loading || !data) {
    return (
      <div className="studio-tab-body">
        <div className="studio-card studio-loading">
          <Loader2 size={22} className="ac-spin" style={{ color: "var(--st-purple)" }}/>
        </div>
      </div>
    );
  }

  const renderRow = (key, label) => {
    const s = data.stats[key];
    return (
      <div className="studio-planner-row" key={key} data-testid={`planner-${key}`}>
        <div className="studio-planner-row-head">
          <strong>{label}</strong>
          <span>{s.published + s.scheduled} of {s.target}</span>
        </div>
        <div className="studio-planner-bar">
          <div className="studio-planner-bar-fill" style={{ width: `${Math.min(100, s.publish_rate)}%` }}/>
        </div>
        <ul className="studio-planner-stats">
          <li><CheckCircle2 size={12}/> {s.published} published</li>
          <li><Calendar size={12}/> {s.scheduled} scheduled</li>
          <li><Layers size={12}/> {s.in_progress} in progress</li>
          {s.missed > 0 && <li className="studio-planner-stat--bad"><AlertTriangle size={12}/> {s.missed} missed</li>}
          <li>{s.remaining} remaining</li>
        </ul>
      </div>
    );
  };

  return (
    <div className="studio-tab-body">
      <div className="studio-card">
        <h3>Monthly planner · {data.month}</h3>
        <p className="studio-card-sub">
          Live progress against the targets set in this channel&apos;s Configuration tab.
        </p>
        <div className="studio-planner">
          {renderRow("long", "Long videos")}
          {renderRow("short", "Shorts")}
        </div>
      </div>
    </div>
  );
}


function VoiceIdeaButton({ channelId, onAdded }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useState({ rec: null, chunks: [] })[0];

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recRef.chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) recRef.chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recRef.chunks, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "voice-idea.webm");
        setBusy(true);
        try {
          const r = await axios.post(
            `${API}/api/studio/voice/idea?channel_id=${channelId}`,
            fd,
            { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
          );
          toast.success(`Idea added: ${r.data.title}`);
          onAdded && onAdded();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Voice idea failed");
        } finally { setBusy(false); }
      };
      recRef.rec = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      toast.error("Microphone permission denied");
    }
  };
  const stop = () => {
    if (recRef.rec && recRef.rec.state === "recording") recRef.rec.stop();
    setRecording(false);
  };

  return (
    <button
      className={recording ? "studio-btn-primary" : "studio-btn-ghost"}
      onClick={recording ? stop : start}
      disabled={busy}
      data-testid="voice-idea-btn"
      title="Record a voice note — Claude will turn it into a structured idea"
    >
      {busy ? <Loader2 size={14} className="ac-spin"/> :
       recording ? <Square size={14}/> : <Mic size={14}/>}
      {busy ? "Transcribing…" : recording ? "Stop" : "Voice idea"}
    </button>
  );
}
